import { useState, useEffect, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Loader2, Users, GraduationCap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";

const ParentModeSelector = forwardRef<HTMLDivElement>((_, ref) => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) fetchChildren();
  }, [user]);

  const fetchChildren = async () => {
    const { data } = await supabase
      .from("children")
      .select("*, classes(name)")
      .eq("parent_id", user!.id)
      .order("full_name");
    setChildren(data || []);
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div ref={ref} className="min-h-[70vh] flex flex-col items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <h1 className="text-3xl font-bold text-foreground mb-2">
          مرحباً {profile?.full_name?.split(" ")[0] || "بك"} 👋
        </h1>
        <p className="text-muted-foreground">اختر الواجهة التي تريد الدخول إليها</p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-2xl">
        {/* Parent Dashboard Card */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card
            className="cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02] border-2 hover:border-primary group"
            onClick={() => navigate("/dashboard/parent-home")}
          >
            <CardContent className="p-8 text-center space-y-4">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto group-hover:bg-primary/20 transition-colors">
                <Users className="w-10 h-10 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-foreground">👨‍👩‍👧 لوحة ولي الأمر</h2>
                <p className="text-sm text-muted-foreground mt-2">
                  متابعة الحضور والغياب، الواجبات، التقارير، الفواتير والرسائل
                </p>
              </div>
              <Badge variant="secondary" className="text-xs">
                إدارة ومتابعة
              </Badge>
            </CardContent>
          </Card>
        </motion.div>

        {/* Student Mode Card */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="border-2 hover:border-amber-400 transition-all">
            <CardContent className="p-8 text-center space-y-4">
              <div className="w-20 h-20 rounded-full bg-amber-100 dark:bg-amber-950/30 flex items-center justify-center mx-auto">
                <GraduationCap className="w-10 h-10 text-amber-500" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-foreground">👦 لوحة الطالب</h2>
                <p className="text-sm text-muted-foreground mt-2">
                  واجهة مبسطة للطفل: الدروس، الواجبات، الامتحانات والألعاب
                </p>
              </div>

              {children.length === 0 ? (
                <p className="text-xs text-muted-foreground">لا يوجد أطفال مرتبطين</p>
              ) : (
                <div className="space-y-2 mt-2">
                  {children.map((child, i) => (
                    <motion.div
                      key={child.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 + i * 0.1 }}
                    >
                      <Card
                        className="cursor-pointer hover:shadow-md transition-all hover:scale-[1.02] border hover:border-amber-400"
                        onClick={() => navigate(`/dashboard/student/${child.id}`)}
                      >
                        <CardContent className="p-3 flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={child.photo_url || ""} />
                            <AvatarFallback className="bg-amber-100 text-amber-700 font-bold text-sm">
                              {child.full_name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 text-right">
                            <p className="font-semibold text-foreground text-sm">{child.full_name}</p>
                            {child.classes && (
                              <p className="text-xs text-muted-foreground">{child.classes.name}</p>
                            )}
                          </div>
                          <span className="text-lg">🎮</span>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
});

ParentModeSelector.displayName = "ParentModeSelector";

export default ParentModeSelector;
